import { OrderedMap } from 'immutable';
import { addCondition, getConditionKey, isLogicGroup, LogicGroupNames, makeLogicGroup } from 'customer-data-filters-ui/filterQueryFormat/logic/LogicGroup';
import getIn from 'transmute/getIn';
import { WistiaIntegrationFamilies } from 'customer-data-filters-ui/converters/listSegClassic/ListSegConstants';
import { testDynamicFilterFamily } from 'customer-data-filters-ui/filterQueryFormat/DynamicFilterFamilies';
import { INTEGRATION } from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { ObjectTypeIdRegex } from 'customer-data-filters-ui/filterQueryFormat/ObjectTypeId';
export const shouldAllowMultipleAndGroupsForFilterFamily = filterFamily => {
  if (typeof filterFamily !== 'string') {
    return false;
  }
  return Boolean(WistiaIntegrationFamilies.includes(filterFamily) || testDynamicFilterFamily(INTEGRATION, filterFamily) || ObjectTypeIdRegex.test(filterFamily));
};
export const getAndConditionKey = (condition, group) => {
  const conditionFilterFamily = getIn(['filterFamily'], condition);
  if (isLogicGroup(condition) && shouldAllowMultipleAndGroupsForFilterFamily(conditionFilterFamily)) {
    return [conditionFilterFamily, getIn(['conditions'], group).size].join('__');
  }
  return getConditionKey(condition, group);
};
const isRootLevelGroup = group => {
  return group._isRoot;
};
const setRoot = group => {
  return group.set('_isRoot', true);
};
const FilterLogicGroup = makeLogicGroup('FilterLogicGroup', OrderedMap());
const createAndGroup = (...conditions) => {
  return conditions.reduce((group, condition) => addCondition(condition, group), FilterLogicGroup().set('logicGroupName', LogicGroupNames.AND));
};
const createOrGroup = (...conditions) => {
  return conditions.reduce((group, condition) => addCondition(condition, group), FilterLogicGroup().set('logicGroupName', LogicGroupNames.OR));
};
const createRootLevelAndGroup = (...conditions) => {
  return conditions.reduce((group, condition) => addCondition(condition, group), setRoot(FilterLogicGroup().set('logicGroupName', LogicGroupNames.AND)));
};
const createRootLevelOrGroup = (...conditions) => {
  return conditions.reduce((group, condition) => addCondition(condition, group), setRoot(FilterLogicGroup().set('logicGroupName', LogicGroupNames.OR)));
};
export const And = {
  of: createAndGroup,
  ofRoot: createRootLevelAndGroup
};
export const Or = {
  of: createOrGroup,
  ofRoot: createRootLevelOrGroup
};

/**
 * Inserts a filter group into a specific position in the root level of the filter query.
 * Maintains entries in an OrderedMap with keys generated as zero-based indices.
 *
 * @example
 * // Example: Inserting a filter group into at specific position
 *
 * // Initial filter with no filter groups
 * const group = Or.of();
 *
 * // New filter group to insert
 * const condition = And.of()
 *
 * // Result after insertAtPosition(0, condition, group):
 * FilterLogicGroup {
 *   operator: 'Or',
 *   filterFamily: '0-1',
 *   conditions: OrderedMap({
 *     '0': condition        // Newly inserted group
 *   })
 * }
 *
 *  // Second filter group to insert
 * const condition2 = And.of()
 *
 * * // Result after insertAtPosition(1, condition, group):
 *  * FilterLogicGroup {
 *   operator: 'Or',
 *   filterFamily: '0-1',
 *   conditions: OrderedMap({
 *     '0': condition
 *     '1': condition2        // Newly inserted group
 *   })
 * }
 *
 * @param index - The position to insert the condition at
 * @param condition - The condition to insert (typically an AND group)
 * @param group - The filter group to insert into (typically an OR group)
 * @param filterFamily - Optional filter family, defaults to group's filterFamily
 * @returns Updated FilterLogicGroup with new condition added to conditions OrderedMap
 */
FilterLogicGroup.insertAtPosition = function insertAtPosition(index, condition, group, filterFamily = group.get('filterFamily')) {
  return group.update('conditions', conditions => {
    let logicGroup = isLogicGroup(condition) ? condition : And.of(condition).set('filterFamily', filterFamily);
    if (group.filterFamily !== filterFamily) {
      logicGroup = And.of(logicGroup).set('filterFamily', group.filterFamily);
    }
    const entries = conditions.valueSeq().toArray();
    const newEntries = [...entries.slice(0, index), logicGroup, ...entries.slice(index)];
    return OrderedMap(newEntries.map((value, i) => [`${i}`, value]));
  });
};

/**
 * Inserts filters into a filter group
 * Maintains conditions in an OrderedMap with keys generated from field names and operators.
 *
 * @example
 * // Example 1: Empty group + first Condition
 *
 * const group = And.of()
 * const condition1 = NotKnown {
 *   field: DSFieldRecord {
 *     name: 'notes_last_contacted',
 *     displayType: 'datetime',
 *     fieldType: 'date'
 *   }
 * };
 *
 * // Result after insertInGroup:
 * FilterLogicGroup {
 *   operator: 'And',
 *   conditions: OrderedMap({
 *     'notes_last_contacted Known 0': condition1
 *   })
 * }
 *
 * // Example 2: Adding second condition to existing group
 * const condition = EqualTimePoint {
 *   field: DSFieldRecord {
 *     name: 'hs_feedback_last_survey_date',
 *     displayType: 'datetime',
 *     fieldType: 'date'
 *   },
 *   value: Moment,
 *   hasTimezones: true
 * };
 *
 * // Result after insertInGroup:
 * FilterLogicGroup {
 *   operator: 'And',
 *   conditions: OrderedMap({
 *     'notes_last_contacted NotKnown 0': condition1,
 *     'hs_feedback_last_survey_date EqualTimePoint 1': condition2
 *   })
 * }
 *
 * @param condition - The condition to insert (e.g., NotKnown, EqualTimePoint)
 * @param group - The FilterLogicGroup group to insert into
 * @param filterFamily - Optional filter family, defaults to group's filterFamily
 * @returns Updated FilterLogicGroup with new condition added to conditions OrderedMap
 */
FilterLogicGroup.insertInGroup = function insertInGroup(condition, group, filterFamily = group.filterFamily) {
  const logicGroup = (group === null || group === void 0 ? void 0 : group.logicGroupName) === 'Or' ? Or : And;
  const emptyGroup = logicGroup.of().set('filterFamily', filterFamily);
  const setInGroup = (g = emptyGroup) => {
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 2 arguments, but got 3.
    const conditionKey = getAndConditionKey(condition, g, filterFamily);
    return g.setIn(['conditions', conditionKey], condition);
  };
  const newGroupKey = shouldAllowMultipleAndGroupsForFilterFamily(filterFamily) ? [filterFamily, getIn(['conditions'], group).size].join('__') : filterFamily;
  return filterFamily === group.filterFamily ? setInGroup(group) : group.updateIn(['conditions', newGroupKey], setInGroup);
};
addCondition.implement(FilterLogicGroup, (condition, group, filterFamily) => {
  // insert filter group into root level
  if (isRootLevelGroup(group)) {
    return FilterLogicGroup.insertAtPosition(group.conditions.size, condition, group, filterFamily);
  }
  // insert filter in filter group
  return FilterLogicGroup.insertInGroup(condition, group, filterFamily);
});
export default FilterLogicGroup;